import { useReactiveVar } from "@apollo/client";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useMemo } from "react";
import { useWindowDimensions } from "react-native";
import type { IMessage } from "react-native-gifted-chat";

import ThreadSidebarView from "./ThreadSidebarView";
import type { ListRenderItem } from "@/components/atoms/FlashList";
import ThreadListItem from "@/features/knowledge-graph/components/molecules/ThreadListItem";
import { useThreadOperations } from "@/features/knowledge-graph/hooks/useThreadOperations";
import {
  sortThreadsByLastAccessed,
  transformConversationMessagesToIMessages,
} from "@/features/knowledge-graph/utils/threadUtils";

import {
  GetThreadQuery,
  useGetThreadLazyQuery,
  UserThread,
  useSearchMyThreadsQuery,
} from "@/generated/graphql";
import {
  selectedThreadIdVar,
  threadHistoryVar,
  messagesVar,
  sessionIdVar,
} from "@/stores/knowledgeGraph";

/**
 * Validate GraphQL query result and check for errors
 * @param result - The complete result object from GraphQL query execution
 * @param result.data - GraphQL query result data containing thread information
 * @param result.error - Apollo error object if query failed
 * @param result.errors - Array of GraphQL-level errors from the server
 * @param result.loading - Whether the query is currently executing
 * @returns True if validation passes and data is available, false otherwise
 */
const validateQueryResult = (result: {
  data?: GetThreadQuery;
  error?: Error;
  errors?: readonly { message?: string }[];
  loading: boolean;
}): boolean => {
  const { data, error, errors, loading } = result;

  if (error) {
    console.error("ThreadSidebar: GraphQL error:", error);
    if (!data?.getThread) {
      return false;
    }
  }

  if (errors && errors.length > 0) {
    console.error("ThreadSidebar: GraphQL errors array:", errors);
    const hasEnumError = errors.some(
      err =>
        err.message?.includes("ConversationRole") ||
        err.message?.includes("cannot represent value")
    );
    if (!hasEnumError || !data?.getThread) {
      return false;
    }
    console.log(
      "ThreadSidebar: Continuing despite enum error since we have thread data"
    );
  }

  if (loading) {
    console.log("ThreadSidebar: Query still loading...");
    return false;
  }

  if (!data) {
    console.error("ThreadSidebar: No data returned from query");
    return false;
  }

  if (!data.getThread) {
    console.error("ThreadSidebar: No getThread in response data");
    return false;
  }

  return true;
};

/**
 * Construct messages from lastQuestion and lastAnswer fields
 * @param threadId - The unique identifier for the thread being processed
 * @param thread - The thread object containing lastQuestion and lastAnswer fields
 * @returns Array of IMessage objects constructed from the last question and answer
 */
const constructMessagesFromLastQA = (
  threadId: string,
  thread: NonNullable<GetThreadQuery["getThread"]>
): IMessage[] => {
  console.log(
    "ThreadSidebar: Found lastQuestion and lastAnswer - constructing messages"
  );
  console.log("ThreadSidebar: lastQuestion:", thread.lastQuestion);
  console.log("ThreadSidebar: lastAnswer:", thread.lastAnswer);

  return [
    {
      _id: `${threadId}-question`,
      text: thread.lastQuestion || "",
      createdAt: new Date(thread.createdAt),
      user: {
        _id: "user",
        name: "You",
      },
    },
    {
      _id: `${threadId}-answer`,
      text: thread.lastAnswer || "",
      createdAt: new Date(thread.updatedAt),
      user: {
        _id: "ai",
        name: "Assistant",
      },
    },
  ];
};

/**
 * Props for the ThreadSidebar container component
 */
interface ThreadSidebarContainerProps {
  isVisible: boolean;
  onClose: () => void;
  isDesktop: boolean;
}

/**
 * Container component for the thread sidebar that manages chat thread history and selection
 * @remarks
 * Uses Apollo reactive variables for state and useThreadOperations hook for operations.
 * Uses React Native's useWindowDimensions hook for cross-platform window size detection,
 * replacing web-only window.innerWidth API.
 * @param root0 - Component props
 * @param root0.isVisible - Whether the sidebar is currently visible
 * @param root0.onClose - Callback to close the sidebar
 * @param root0.isDesktop - Whether the app is running on desktop (affects layout)
 */
const ThreadSidebarContainer = ({
  isVisible,
  onClose,
  isDesktop,
}: ThreadSidebarContainerProps) => {
  // Window dimensions for responsive behavior
  const { width } = useWindowDimensions();
  const router = useRouter();

  // Reactive variable state
  const selectedThreadId = useReactiveVar(selectedThreadIdVar);
  const threadHistory = useReactiveVar(threadHistoryVar);

  // Operations from hooks
  const {
    setSelectedThreadId,
    updateThreadHistory,
    createNewThread,
    loadThreadMessages,
  } = useThreadOperations();

  // GraphQL query for fetching threads
  const {
    data: threadsData,
    loading: isLoadingThreads,
    error: threadsError,
  } = useSearchMyThreadsQuery({
    variables: {
      query: undefined, // Explicitly pass undefined to get all threads
    },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  // Lazy query for fetching thread messages
  const [getThreadMessages] = useGetThreadLazyQuery({
    fetchPolicy: "network-only", // Force fresh fetch, bypass cache
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      console.log("ThreadSidebar: GraphQL Query completed successfully:", data);
      console.log(
        "ThreadSidebar: Raw Apollo result structure:",
        JSON.stringify(data, null, 2)
      );
    },
    onError: error => {
      console.error("ThreadSidebar: GraphQL onError callback:", error);
      console.error(
        "ThreadSidebar: Network error details:",
        error.networkError
      );
      console.error("ThreadSidebar: GraphQL errors:", error.graphQLErrors);
    },
  });

  // Extract threads from GraphQL response and filter out null/undefined nodes
  const rawThreads = useMemo(
    () =>
      threadsData?.searchMyThreads?.edges
        ?.map(edge => edge.node)
        .filter(Boolean) || [],
    [threadsData]
  );

  // Process threads: filter, sort and store in chat store
  const processedThreads = useMemo(() => {
    const filteredThreads = rawThreads.filter(thread => {
      const firstQuestion = (thread.firstQuestion || "").toLowerCase();
      const title = (thread.title || "").toLowerCase();

      // Filter out system-generated threads
      const shouldFilter = [
        firstQuestion.startsWith("generate"),
        firstQuestion.startsWith("instruction"),
        firstQuestion.startsWith("task"),
        firstQuestion.startsWith("respond in"), // AI player summaries (non-English)
        firstQuestion.includes("scouting summary"), // AI player summaries (all languages)
        firstQuestion.includes("generate a concise"), // AI player summaries (all languages)
        title.startsWith("generate"),
        title.startsWith("instruction"),
        title.startsWith("task"),
        title.startsWith("respond in"), // AI player summaries (non-English)
        title.includes("scouting summary"), // AI player summaries (all languages)
        title.includes("generate a concise"), // AI player summaries (all languages)
      ].some(condition => condition);

      return !shouldFilter;
    });
    return sortThreadsByLastAccessed(filteredThreads);
  }, [rawThreads]);

  // Handle thread selection
  const handleThreadSelect = useCallback(
    async (threadId: string) => {
      // Set the selected thread - this will use the threadId as sessionId for future messages
      setSelectedThreadId(threadId);

      // Update the sessionId to this thread's ID for future messages
      sessionIdVar(threadId);

      // Clear current messages when switching threads
      messagesVar([]);

      // Fetch thread messages for this thread
      try {
        console.log(
          "ThreadSidebar: Calling getThreadMessages with threadId:",
          threadId
        );

        const result = await getThreadMessages({
          variables: {
            threadId,
          },
        });

        console.log("ThreadSidebar: Full getThreadMessages result:", result);

        // Validate query result and handle errors
        if (!validateQueryResult(result)) {
          return;
        }

        const thread = result.data!.getThread;
        if (!thread) {
          console.error("ThreadSidebar: Thread is null after validation");
          return;
        }

        console.log("ThreadSidebar: Thread object:", thread);
        console.log(
          "ThreadSidebar: Full thread data:",
          JSON.stringify(thread, null, 2)
        );
        console.log("ThreadSidebar: Thread.id:", thread.id);
        console.log("ThreadSidebar: Thread.messageCount:", thread.messageCount);
        console.log("ThreadSidebar: Thread.lastQuestion:", thread.lastQuestion);
        console.log("ThreadSidebar: Thread.lastAnswer:", thread.lastAnswer);
        console.log("ThreadSidebar: Thread.metadata:", thread.metadata);

        if (!thread.metadata) {
          console.error("ThreadSidebar: Thread metadata is null or undefined");
          return;
        }

        const { messages } = thread.metadata;
        console.log("ThreadSidebar: Metadata.messages:", messages);
        console.log("ThreadSidebar: Type of messages:", typeof messages);
        console.log(
          "ThreadSidebar: Is messages array?",
          Array.isArray(messages)
        );

        if (Array.isArray(messages) && messages.length > 0) {
          console.log(
            "ThreadSidebar: Found messages in metadata, count:",
            messages.length
          );
          console.log("ThreadSidebar: Raw conversation messages:", messages);

          // Transform ConversationMessage to IMessage format
          const transformedMessages =
            transformConversationMessagesToIMessages(messages);
          console.log(
            "ThreadSidebar: Transformed messages:",
            transformedMessages
          );

          // Load the messages into the chat
          console.log("ThreadSidebar: Loading messages into chat");
          loadThreadMessages(transformedMessages);

          // Verify messages were loaded
          const currentMessages = messagesVar();
          console.log(
            "ThreadSidebar: Messages after loading:",
            currentMessages.length,
            "messages"
          );
        } else {
          console.log(
            "ThreadSidebar: No messages in metadata, but checking lastQuestion/lastAnswer fields"
          );

          // SOLUTION: Construct messages from lastQuestion and lastAnswer
          if (thread.lastQuestion && thread.lastAnswer) {
            const constructedMessages = constructMessagesFromLastQA(
              threadId,
              thread
            );
            console.log(
              "ThreadSidebar: Constructed messages:",
              constructedMessages
            );
            loadThreadMessages(constructedMessages);

            // Verify messages were loaded
            const currentMessages = messagesVar();
            console.log(
              "ThreadSidebar: Messages after loading:",
              currentMessages.length,
              "messages"
            );
          } else {
            console.log(
              "ThreadSidebar: No lastQuestion/lastAnswer found either"
            );
            loadThreadMessages([]);
          }
        }
      } catch (error) {
        console.error("ThreadSidebar: Failed to load thread messages:", error);
        if (error instanceof Error) {
          console.error("ThreadSidebar: Error message:", error.message);
          console.error("ThreadSidebar: Error stack:", error.stack);
        }
      }

      // Close sidebar on mobile after selection
      if (width < 768) {
        onClose();
      }
    },
    [setSelectedThreadId, getThreadMessages, loadThreadMessages, onClose, width]
  );

  // Handle creating new thread
  const handleNewThread = useCallback(() => {
    createNewThread().catch(() => {
      // Ignore storage errors - new thread creation will still work with in-memory state
    });
    // Remove stale ?q= param so prefill doesn't re-trigger
    router.replace("/knowledge-graph");
    // Close sidebar on mobile after creating new thread
    if (width < 768) {
      onClose();
    }
  }, [createNewThread, onClose, width, router]);

  // Memoized error message
  const errorMessage = useMemo(() => {
    if (!threadsError) return null;
    return threadsError.message || "Failed to load conversation history";
  }, [threadsError]);

  // Memoized empty state message
  const emptyStateMessage = useMemo(() => {
    if (isLoadingThreads) return null;
    return "No conversation history yet. Start a new conversation to see it here.";
  }, [isLoadingThreads]);

  // Memoized key extractor for FlashList
  const keyExtractor = useCallback((item: UserThread) => item.id, []);

  // Memoized render item for FlashList
  const renderItem = useCallback<ListRenderItem<UserThread>>(
    ({ item }) => (
      <ThreadListItem
        thread={item}
        isSelected={item.id === selectedThreadId}
        onSelect={handleThreadSelect}
      />
    ),
    [selectedThreadId, handleThreadSelect]
  );

  // Update thread history in store when threads change
  useEffect(() => {
    updateThreadHistory(processedThreads as UserThread[]);
  }, [processedThreads, updateThreadHistory]);

  return (
    <ThreadSidebarView
      isVisible={isVisible}
      onClose={onClose}
      threads={threadHistory}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      onNewThread={handleNewThread}
      isLoading={isLoadingThreads}
      errorMessage={errorMessage}
      emptyStateMessage={emptyStateMessage}
      isDesktop={isDesktop}
    />
  );
};

export default ThreadSidebarContainer;
